'use strict';
/**
 * Em qual sistema (main ou med) a empresa pode ser usada — `company.systemLine`, #1258/#1259.
 *
 * As duas linhas usam o mesmo projeto Firebase, mas gravam o financeiro em formatos diferentes
 * (sinal dos impostos, movimentações). Uma empresa que já está no financeiro novo e é aberta pela
 * main passa a ser lida e gravada no formato antigo — foi assim que a COOPANEST-ES criou cobranças
 * pela main em 18/09/2026 (#1242). O layout usa esta regra para bloquear o usuário comum e avisar
 * o super admin; o script de conversão de `transfers` (#1257) a usa para só gravar em empresa
 * marcada como MED.
 *
 * Ausente (ou valor desconhecido) vale BOTH: toda empresa continua como está até alguém mudar.
 *
 * Filial que NÃO é cooperado cliente (`isFilial` sem `flags.isFilialClient`) opera sobre os dados
 * da matriz, então segue o `systemLine` da MATRIZ e o da própria filial é ignorado. Enquanto a
 * matriz não é conhecida (ainda carregando, ou sem permissão de leitura), vale BOTH: a regra não
 * bloqueia por falta de dado.
 */
const {CompanySystemLine} = require('./status');

exports.default = exports;

/**
 * A empresa segue o `systemLine` da matriz? Sim para filial que não é cooperado cliente — o mesmo
 * critério que o resto do sistema usa para tratar a filial como extensão da matriz.
 * @param {*} company doc da empresa (ou vazio)
 * @returns {Boolean}
 */
exports.companyFollowsMatrizSystemLine = function(company) {
  return !!company?.isFilial && !!company?.matrizId && !company?.flags?.isFilialClient;
};

function normalize(line) {
  if (line === CompanySystemLine.MED || line === CompanySystemLine.MAIN) {
    return line;
  }
  return CompanySystemLine.BOTH;
}

/**
 * Valor efetivo do campo.
 * @param {*} company doc da empresa (ou vazio)
 * @param {*} [matriz] doc da matriz (basta `{id, systemLine}`); usado só quando a empresa
 *   segue a matriz, e só se for a matriz dela (`matriz.id === company.matrizId`)
 * @returns {String} um dos `CompanySystemLine`
 */
exports.resolveCompanySystemLine = function(company, matriz = null) {
  if (exports.companyFollowsMatrizSystemLine(company)) {
    return matriz?.id === company.matrizId ? normalize(matriz.systemLine) : CompanySystemLine.BOTH;
  }
  return normalize(company?.systemLine);
};

/**
 * A empresa pode ser usada neste sistema? Sem empresa, sim — não há o que bloquear.
 * @param {*} company doc da empresa (ou vazio)
 * @param {Boolean} isMed `appConfig.isMed` do sistema em execução
 * @param {*} [matriz] ver `resolveCompanySystemLine`
 * @returns {Boolean}
 */
exports.isCompanyAllowedOnLine = function(company, isMed, matriz = null) {
  if (!company) {
    return true;
  }
  const line = exports.resolveCompanySystemLine(company, matriz);
  if (line === CompanySystemLine.BOTH) {
    return true;
  }
  return line === (isMed ? CompanySystemLine.MED : CompanySystemLine.MAIN);
};

/**
 * Para qual sistema a empresa deve ir quando não pode ser usada neste; `null` quando pode.
 * @param {*} company doc da empresa (ou vazio)
 * @param {Boolean} isMed `appConfig.isMed` do sistema em execução
 * @param {*} [matriz] ver `resolveCompanySystemLine`
 * @returns {String|null} `CompanySystemLine.MED`, `CompanySystemLine.MAIN` ou null
 */
exports.expectedSystemLine = function(company, isMed, matriz = null) {
  if (exports.isCompanyAllowedOnLine(company, isMed, matriz)) {
    return null;
  }
  return exports.resolveCompanySystemLine(company, matriz);
};
